import classNames from 'classnames';

import {
  isChallengeBundleItem,
  isExtraOfferBundleItem,
  isFreeSpinsBundleItem,
  isMetaGameBundleItem,
  isMetaGameFeatureBundleItem,
  isRipsPackBundleItem,
  isSnappyGiftBundleItem,
} from '../../stores/rewards/helpers';
import type { BundleItem } from '../../stores/rewards/types';
import ChallengeRewardComponent from '../EventTriggerDialog/gifts/GiftLayout/components/ChallengeRewardComponent/ChallengeRewardComponent';
import ExtraOfferRewardComponent from '../EventTriggerDialog/gifts/GiftLayout/components/ExtraOfferRewardComponent/ExtraOfferRewardComponent';
import { FRBWidget } from '../FRBWidget/FRBWidget';
import ImageWithFallback from '../ImageWithFallback/ImageWithFallback';
import { MetaGameWidget } from '../MetaGameWidget/MetaGameWidget';
import type { MetagameWidgetDirection } from '../MetaGameWidget/types';
import { SnappyGiftReward } from '../SnappyGiftReward/SnappyGiftReward';

import classes from './BundleItemsRenderer.module.scss';

type BundleItemsRendererProps = {
  bundleItems: BundleItem[];
  className?: string;
  metagameIconClassName?: string;
  metagameTextContainerClassName?: string;
  metagameTextClassName?: string;
  frbWidgetWidth?: number;
  metaGameWidgetWidth?: number;
  direction?: MetagameWidgetDirection;
  hidePlus?: boolean;
  dataTestId?: string;
  snappyGiftWidgetWidth?: number;
  wrapperClassName?: string;
  challengeClassName?: string;
  challengeWidgetWidth?: number;
};

export function BundleItemsRenderer({
  bundleItems,
  className,
  metagameIconClassName,
  metagameTextContainerClassName,
  metagameTextClassName,
  frbWidgetWidth,
  metaGameWidgetWidth,
  snappyGiftWidgetWidth,
  direction,
  hidePlus,
  dataTestId,
  wrapperClassName,
  challengeClassName,
  challengeWidgetWidth,
}: BundleItemsRendererProps) {
  return (
    <div
      className={classNames(
        classes.bundleItemsRenderer,
        direction === 'vertical' && classes.vertical,
        wrapperClassName,
      )}
      data-testid={dataTestId}
    >
      {bundleItems.map((item, index) => {
        const isLastItem = index === bundleItems.length - 1;

        return (
          <div
            key={index}
            className={classNames(classes.bundleItem, className)}
            data-testid={dataTestId ? `${dataTestId}-${index}` : undefined}
          >
            {isFreeSpinsBundleItem(item) && (
              <FRBWidget
                betAmount={item.bundlePayload.bet}
                coinType={item.bundlePayload.coinType}
                gameImage={item.bundlePayload.gameImageUrl}
                maxPrize={item.bundlePayload.maxPrize}
                provider={item.bundlePayload.provider}
                rounds={item.bundlePayload.rounds}
                type={item.bundlePayload.type}
                width={frbWidgetWidth}
              />
            )}
            {isMetaGameBundleItem(item) && (
              <MetaGameWidget
                amount={item.bundlePayload.amount}
                className={classes.metaGameWidget}
                coinType={item.bundlePayload.coinType}
                currencyName={item.bundlePayload.currencyName}
                currencySymbol={item.bundlePayload.currencySymbol}
                direction={direction || 'horizontal'}
                metagameIconClassName={metagameIconClassName}
                metagameTextClassName={metagameTextClassName}
                metagameTextContainerClassName={metagameTextContainerClassName}
                metagameTypeId={item.bundlePayload.metagameTypeId}
                width={metaGameWidgetWidth}
              />
            )}
            {isSnappyGiftBundleItem(item) && (
              <SnappyGiftReward snappyGiftPayload={item.bundlePayload} width={snappyGiftWidgetWidth} />
            )}

            {isMetaGameFeatureBundleItem(item) && item.bundlePayload.symbolUrl && (
              <MetaGameWidget
                amount={item.bundlePayload.amount}
                className={classes.metaGameWidget}
                currencySymbol={item.bundlePayload.symbolUrl}
                direction={direction || 'horizontal'}
                metagameIconClassName={metagameIconClassName}
                metagameTextClassName={metagameTextClassName}
                metagameTextContainerClassName={metagameTextContainerClassName}
                width={metaGameWidgetWidth}
              />
            )}
            {isChallengeBundleItem(item) && (
              <ChallengeRewardComponent
                className={challengeClassName}
                symbolUrl={item.bundlePayload.symbolUrl}
                width={challengeWidgetWidth}
              />
            )}
            {isExtraOfferBundleItem(item) && (
              <ExtraOfferRewardComponent
                extraPercentage={item.bundlePayload.extraPercentage}
                rewardImageUrl={item.bundlePayload.rewardImageUrl}
              />
            )}
            {isRipsPackBundleItem(item) && item.bundlePayload.imageUrl && (
              <div className={classes.ripsPack}>
                <ImageWithFallback
                  alt={item.bundlePayload.name || 'Rips pack'}
                  className={classes.ripsPackImage}
                  src={item.bundlePayload.imageUrl}
                />
                {(item.bundlePayload.quantity ?? 1) > 1 && (
                  <span className={classes.ripsPackQuantity}>×{item.bundlePayload.quantity}</span>
                )}
              </div>
            )}
            {!isLastItem && !hidePlus && <div className={classes.plus}>+</div>}
          </div>
        );
      })}
    </div>
  );
}
